import { ScatterSeriesOption, TooltipComponentOption } from "echarts";
import { isEmpty, truncate } from "lodash";
import { Theme } from "@mui/material";

import { calculateBubbleSize } from "@/Components/v2/molecule/BubbleChart/calculateBubbleSize";
import { QuadrantLabel } from "@/Components/v2/molecule/BubbleChart/types";
import { getQuadrantColor } from "./getQuadrantColor";
import { getQuadrantLabelConfigs } from "./getQuadrantLabelConfigs";
import { ThresholdLines } from "@/Components/v2/molecule/BubbleChart/types";
import { getBubbleChartTooltip } from "./getBubbleChartTooltip";
import { ExtendedMarketingRevenueRangeTimeSeries } from "@/pages/scenario-builder/v3/api/v2/getScenarioTimeSeriesResults";

interface GetBubbleChartOptions {
  thresholdLines: ThresholdLines;
  timeSeriesResults: ExtendedMarketingRevenueRangeTimeSeries[];
  theme: Theme;
  numberFormatter: (value: number) => string;
}

export const getBubbleChart = ({
  thresholdLines,
  timeSeriesResults,
  theme,
  numberFormatter,
}: GetBubbleChartOptions): {
  scatterSeries: ScatterSeriesOption;
  quadrantLabels: QuadrantLabel[];
  tooltipOptions: TooltipComponentOption;
} | null => {
  const filteredTimeSeriesResults = (timeSeriesResults ?? []).filter((result) => {
    return (
      result.spend > 0 &&
      result.confidenceInterval !== undefined &&
      result.changeFromHistoricSpend !== undefined
    );
  });

  if (isEmpty(filteredTimeSeriesResults)) {
    return null;
  }

  const allSpends = filteredTimeSeriesResults.map((result) => result.spend);
  const minSpend = Math.min(...allSpends);
  const maxSpend = Math.max(...allSpends);

  const seriesData = filteredTimeSeriesResults.map((result) => {
    const { confidenceInterval, changeFromHistoricSpend } = result;
    return {
      name: result.channel ?? "",
      value: [confidenceInterval, changeFromHistoricSpend, result.spend],
      itemStyle: {
        color: getQuadrantColor(confidenceInterval!, changeFromHistoricSpend!, thresholdLines),
      },
    };
  });

  const scatterSeries: ScatterSeriesOption = {
    name: "Likelihood by Channel",
    type: "scatter",
    data: seriesData,
    z: -1,
    symbolSize: (data: number[]) => {
      const spend = data[2] ?? 0;
      return calculateBubbleSize(spend, minSpend, maxSpend);
    },
    label: {
      show: true,
      position: "inside",
      formatter: (param: any) => {
        return truncate(param.name, { length: 16 });
      },
      color: theme.palette.mode === "dark" ? "#000000" : "#FFFFFF",
      fontWeight: 600,
      opacity: 1,
    },
  };

  return {
    scatterSeries,
    quadrantLabels: getQuadrantLabelConfigs(),
    tooltipOptions: getBubbleChartTooltip(theme, numberFormatter),
  };
};
